import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus } from "lucide-react";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import {
  useCreatePriceRemarkReasonMutation,
  useUpdatePriceRemarkReasonMutation,
} from "@/services/price-remark/justification-reasons";
import { PriceRemarkReason } from "@/services/price-remark/types";

import {
  CreateReasonFormValues,
  EditReasonFormValues,
  createReasonSchema,
  editReasonSchema,
} from "../data/schema";

const CATALOG_CONTEXT = "CNF";

function errorMessage(error: unknown, fallback: string): string {
  return (
    (error as { data?: { message?: string } })?.data?.message ?? fallback
  );
}

export function CreateReasonDialog() {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [createReason, { isLoading }] = useCreatePriceRemarkReasonMutation();

  const form = useForm<CreateReasonFormValues>({
    resolver: zodResolver(createReasonSchema),
    defaultValues: { codigo: "", rotulo: "", ordem: 1, ativo: true },
  });

  const onSubmit = async (data: CreateReasonFormValues) => {
    try {
      await createReason({ contexto: CATALOG_CONTEXT, ...data }).unwrap();
      toast({ variant: "success", description: "Motivo criado." });
      form.reset();
      closeRef.current?.click();
    } catch (error) {
      toast({
        variant: "destructive",
        description: errorMessage(error, "Não foi possível criar o motivo."),
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1 h-4 w-4" /> Novo motivo
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo motivo (contexto CNF)</DialogTitle>
          <DialogDescription>
            Código e contexto são imutáveis após criados e nunca reaproveitados.
          </DialogDescription>
        </DialogHeader>

        <form
          id="create-reason-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label>Código</Label>
            <Input
              placeholder="Ex.: NLO"
              {...form.register("codigo")}
              onChange={(event) =>
                form.setValue("codigo", event.target.value.toUpperCase(), {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
            />
            {form.formState.errors.codigo && (
              <p className="text-xs text-red-600">
                {form.formState.errors.codigo.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Rótulo</Label>
            <Input placeholder="Ex.: Peça não localizada" {...form.register("rotulo")} />
            {form.formState.errors.rotulo && (
              <p className="text-xs text-red-600">
                {form.formState.errors.rotulo.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Ordem</Label>
            <Input
              type="number"
              min={1}
              max={999}
              className="w-[120px]"
              {...form.register("ordem", { valueAsNumber: true })}
            />
            {form.formState.errors.ordem && (
              <p className="text-xs text-red-600">
                {form.formState.errors.ordem.message}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Label>Ativo</Label>
            <Controller
              name="ativo"
              control={form.control}
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <button ref={closeRef} type="button" className="hidden" />
          </DialogClose>
          <Button
            type="submit"
            form="create-reason-form"
            disabled={isLoading}
          >
            Criar motivo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditReasonDialog({ reason }: { reason: PriceRemarkReason }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [updateReason, { isLoading }] = useUpdatePriceRemarkReasonMutation();

  const form = useForm<EditReasonFormValues>({
    resolver: zodResolver(editReasonSchema),
    defaultValues: {
      rotulo: reason.rotulo,
      ordem: reason.ordem,
      ativo: reason.ativo,
    },
  });

  useEffect(() => {
    form.reset({
      rotulo: reason.rotulo,
      ordem: reason.ordem,
      ativo: reason.ativo,
    });
  }, [reason, form]);

  const onSubmit = async (data: EditReasonFormValues) => {
    try {
      await updateReason({
        contexto: reason.contexto,
        codigo: reason.codigo,
        body: data,
      }).unwrap();
      toast({ variant: "success", description: "Motivo atualizado." });
      closeRef.current?.click();
    } catch (error) {
      toast({
        variant: "destructive",
        description: errorMessage(error, "Não foi possível atualizar o motivo."),
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Pencil className="mr-1 h-3.5 w-3.5" /> Editar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Editar motivo {reason.codigo} ({reason.contexto})
          </DialogTitle>
          <DialogDescription>
            Editar o rótulo não reescreve o histórico já registrado. Código e
            contexto ficam bloqueados.
          </DialogDescription>
        </DialogHeader>

        <form
          id={`edit-reason-form-${reason.contexto}-${reason.codigo}`}
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Contexto</Label>
              <Input value={reason.contexto} disabled />
            </div>
            <div className="space-y-1.5">
              <Label>Código</Label>
              <Input value={reason.codigo} disabled />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Rótulo</Label>
            <Input {...form.register("rotulo")} />
            {form.formState.errors.rotulo && (
              <p className="text-xs text-red-600">
                {form.formState.errors.rotulo.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Ordem</Label>
            <Input
              type="number"
              min={1}
              max={999}
              className="w-[120px]"
              {...form.register("ordem", { valueAsNumber: true })}
            />
            {form.formState.errors.ordem && (
              <p className="text-xs text-red-600">
                {form.formState.errors.ordem.message}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Label>Ativo</Label>
            <Controller
              name="ativo"
              control={form.control}
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <span className="text-xs text-gray-400">
              Inativo some da lista do operador, mas segue traduzindo o histórico.
            </span>
          </div>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <button ref={closeRef} type="button" className="hidden" />
          </DialogClose>
          <Button
            type="submit"
            form={`edit-reason-form-${reason.contexto}-${reason.codigo}`}
            disabled={isLoading}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
